import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

interface OverlayOwner { id: string; active: boolean }
interface OverlayOpenState { open?: boolean; defaultOpen?: boolean; onOpenChange?: (open: boolean) => void }

const OverlayOwnerContext = createContext<OverlayOwner | null>(null);

/** Portals remain part of their owning surface's interaction and visibility. */
export function OverlayScope({ id, active, children }: OverlayOwner & { children: ReactNode }) {
  const owner = useMemo(() => ({ id, active }), [id, active]);
  return <OverlayOwnerContext value={owner}>{children}</OverlayOwnerContext>;
}

export function useOverlayOwner() { return useContext(OverlayOwnerContext); }

/** Reset an uncontrolled layer on a new owner visit, preserving its trigger DOM. */
export function useScopedOverlayState({ open, defaultOpen, onOpenChange }: OverlayOpenState) {
  const owner = useOverlayOwner();
  const id = owner?.id;
  const active = owner?.active ?? true;
  const [visit, setVisit] = useState({ id, active, open: defaultOpen ?? false });
  const sameVisit = visit.id === id && visit.active === active;
  if (!sameVisit) setVisit({ id, active, open: false });

  return {
    open: owner ? active && (open ?? (sameVisit && visit.open)) : open,
    onOpenChange(next: boolean) {
      if (!active && next) return;
      if (owner) setVisit({ id, active, open: next });
      onOpenChange?.(next);
    },
  };
}
